import React from 'react';
import { AISummarizeKind } from 'Generated/schema';
import { useListAIInferenceStreamsMetadata } from 'Components/AIExplainLogEvents/graphql/listAiInferenceStreamsMetadata.generated';
import useInfiniteScroll from 'Hooks/useInfiniteScroll';
import useUrlParams from 'Hooks/useUrlParams';
import { AISummaryStreamParams } from 'Components/AIExplainLogEvents/useFetchGenericAiSummaryV3';
import { getStreamName } from 'Components/sidesheets/AISummarySidePanel/AISummarySidePanelContent/EditableThreadNameV3';

interface UseAIPageThreadsProps {
  kind: AISummarizeKind;
  alias: string;
}

interface UseAIPageThreadsReturn {
  streamsMetadata: ReturnType<typeof useListAIInferenceStreamsMetadata>['data'];
  isLoadingThreads: boolean;
  hasNextPage: boolean;
  currentThreadTitle: string | null;
  sentinelRef: React.RefObject<HTMLDivElement>;
  fetchMore: () => void;
}

/**
 * Hook for managing AI page thread list state and infinite scroll
 */
export const useAIPageThreadsV3 = ({
  kind,
  alias,
}: UseAIPageThreadsProps): UseAIPageThreadsReturn => {
  const { urlParams } = useUrlParams<AISummaryStreamParams>();
  const {
    data,
    loading: isLoadingThreads,
    fetchMore,
  } = useListAIInferenceStreamsMetadata({
    variables: {
      input: {
        alias,
      },
    },
  });

  // Get current thread title
  const currentThreadTitle = React.useMemo(() => {
    if (!urlParams?.aiStream) {
      return null;
    }
    const streams = data?.aiInferenceStreamsMetadata?.edges?.map(edge => edge.node) || [];
    const currentStream = streams.find(stream => stream.streamId === urlParams.aiStream);
    if (currentStream) {
      return getStreamName(currentStream, kind);
    }
    return null;
  }, [urlParams?.aiStream, data?.aiInferenceStreamsMetadata?.edges, kind]);

  const hasNextPage = Boolean(data?.aiInferenceStreamsMetadata?.pageInfo?.hasNextPage);

  const handleFetchMore = React.useCallback(() => {
    if (hasNextPage && data?.aiInferenceStreamsMetadata?.pageInfo?.endCursor) {
      fetchMore({
        variables: {
          input: {
            alias,
            cursor: data.aiInferenceStreamsMetadata.pageInfo.endCursor,
          },
        },
      });
    }
  }, [hasNextPage, data?.aiInferenceStreamsMetadata?.pageInfo?.endCursor, alias, fetchMore]);

  const { sentinelRef } = useInfiniteScroll<HTMLDivElement>({
    loading: isLoadingThreads,
    threshold: 500,
    onLoadMore: handleFetchMore,
  });

  return {
    streamsMetadata: data,
    isLoadingThreads,
    hasNextPage,
    currentThreadTitle,
    sentinelRef,
    fetchMore: handleFetchMore,
  };
};
